import { useEffect, useState } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS, IS_TEST_ENV } from "@/constants/api";

/**
 * Stripe-billed subscriptions — the dedicated back-office view.
 *
 * These rows are deliberately EXCLUDED from the normal Free Subscriptions screens:
 * Stripe is their source of truth, so this screen is read-only and every row
 * deep-links into the Stripe dashboard where cancel/refund/plan changes belong.
 * The backend enforces the same split (update/delete/end-now return 409 for them).
 *
 * The one write this screen offers is RECONCILE: a live fetch of the user's
 * subscriptions from the Stripe API, re-driven through the same logic as the
 * webhook. It repairs whatever a missed webhook left behind (lapsed-but-paid
 * grant, wrong tier, zombie entitlement, missing customer mapping) — the
 * support answer to "I paid but I'm not PRO".
 */

type StripeSubscriptionRow = {
    id: number;
    userId: number;
    userName: string | null;
    subscriptionType: string; // 'light' (Plus) | 'pro'
    startDate: string | null;
    endDate: string | null;
    active: boolean;
    stripeSubscriptionId: string;
    stripeCustomerId: string | null;
    dashboardUrl: string;
};

type WebhookErrorRow = {
    id: number;
    eventId: string;
    type: string | null;
    userId: number | null;
    subscriptionId: string | null;
    priceId: string | null;
    status: string;
    error: string | null;
    receivedAt: string | null;
};

// Backend hands the live-mode URL; in the test environment the sandbox objects
// live under /test on the dashboard.
const dashboardHref = (row: StripeSubscriptionRow) =>
    IS_TEST_ENV
        ? row.dashboardUrl.replace(
              "dashboard.stripe.com/",
              "dashboard.stripe.com/test/"
          )
        : row.dashboardUrl;

const tierLabel = (t: string) => (t === "light" ? "Plus" : t === "pro" ? "PRO" : t);

const StripeSubscriptionPage = () => {
    const [rows, setRows] = useState<StripeSubscriptionRow[]>([]);
    const [webhookErrors, setWebhookErrors] = useState<WebhookErrorRow[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [reconcilingId, setReconcilingId] = useState<number | null>(null);
    const [reconcileResult, setReconcileResult] = useState<string | null>(null);
    const [manualUserId, setManualUserId] = useState("");

    const fetchAll = async () => {
        try {
            const response = await axiosInstance.get(
                ENDPOINTS.FREESUBSCRIPTIONS.STRIPE
            );
            setRows(response.data);
            setError(null);
        } catch (e) {
            setError("Failed to load Stripe subscriptions");
        }
        try {
            const response = await axiosInstance.get(
                ENDPOINTS.FREESUBSCRIPTIONS.STRIPE_ERRORS
            );
            setWebhookErrors(response.data);
        } catch (e) {
            // Dead-letter panel is best-effort; the main table stays usable.
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reconcile = async (userId: number) => {
        setReconcilingId(userId);
        setReconcileResult(null);
        try {
            const response = await axiosInstance.post(
                `${ENDPOINTS.FREESUBSCRIPTIONS.STRIPE_RECONCILE}/${userId}`
            );
            setReconcileResult(JSON.stringify(response.data, null, 2));
            await fetchAll();
        } catch (e) {
            setReconcileResult(`Reconcile failed for user ${userId}`);
        } finally {
            setReconcilingId(null);
        }
    };

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold mb-1">Stripe Subscriptions</h1>
            <p className="text-sm text-gray-500 mb-6">
                Billed and managed in Stripe — read-only here. Use the dashboard
                link on a row for cancellations, refunds or plan changes. These
                rows never appear under Free Subscriptions. Reconcile re-fetches
                a user's live state from Stripe and repairs the local grant.
            </p>

            {error && <div className="text-red-600 mb-4">{error}</div>}

            {webhookErrors.length > 0 && (
                <div className="mb-6 border border-amber-300 bg-amber-50 rounded p-4">
                    <h2 className="font-semibold text-amber-800 mb-2">
                        Unprocessed webhook events ({webhookErrors.length})
                    </h2>
                    <p className="text-xs text-amber-700 mb-3">
                        Stripe delivered these but processing failed or stalled.
                        Find the affected user (via the subscription in the
                        Stripe dashboard if unresolved) and reconcile them.
                    </p>
                    <table className="min-w-full text-xs">
                        <thead>
                            <tr className="text-left text-amber-700">
                                <th className="py-1 pr-3">Received</th>
                                <th className="py-1 pr-3">Type</th>
                                <th className="py-1 pr-3">User</th>
                                <th className="py-1 pr-3">Subscription</th>
                                <th className="py-1 pr-3">Error</th>
                                <th className="py-1 pr-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {webhookErrors.map((ev) => (
                                <tr key={ev.id} className="align-top">
                                    <td className="py-1 pr-3 whitespace-nowrap">
                                        {ev.receivedAt?.slice(0, 16) || "—"}
                                    </td>
                                    <td className="py-1 pr-3">{ev.type || "—"}</td>
                                    <td className="py-1 pr-3">
                                        {ev.userId ?? "unresolved"}
                                    </td>
                                    <td className="py-1 pr-3 font-mono">
                                        {ev.subscriptionId || "—"}
                                    </td>
                                    <td className="py-1 pr-3 max-w-md break-words">
                                        {ev.error || ev.status}
                                    </td>
                                    <td className="py-1 pr-3">
                                        {ev.userId != null && (
                                            <button
                                                onClick={() => reconcile(ev.userId as number)}
                                                disabled={reconcilingId !== null}
                                                className="text-indigo-600 hover:underline disabled:opacity-50"
                                            >
                                                Reconcile
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mb-6 flex items-center gap-2">
                <input
                    type="number"
                    value={manualUserId}
                    onChange={(e) => setManualUserId(e.target.value)}
                    placeholder="User ID"
                    className="border rounded px-2 py-1 text-sm w-32"
                />
                <button
                    onClick={() => {
                        const uid = parseInt(manualUserId, 10);
                        if (!isNaN(uid)) reconcile(uid);
                    }}
                    disabled={reconcilingId !== null || !manualUserId}
                    className="bg-indigo-600 text-white text-sm rounded px-3 py-1 disabled:opacity-50"
                >
                    {reconcilingId !== null ? "Reconciling…" : "Reconcile user from Stripe"}
                </button>
                <span className="text-xs text-gray-400">
                    Works even when no row exists yet (orphaned checkout: looks the
                    customer up by account email).
                </span>
            </div>

            {reconcileResult && (
                <pre className="mb-6 bg-gray-50 border rounded p-3 text-xs overflow-x-auto max-h-64">
                    {reconcileResult}
                </pre>
            )}

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                        <tr className="text-left text-gray-500">
                            <th className="py-2 pr-4">User</th>
                            <th className="py-2 pr-4">Tier</th>
                            <th className="py-2 pr-4">Status</th>
                            <th className="py-2 pr-4">Start</th>
                            <th className="py-2 pr-4">Paid until</th>
                            <th className="py-2 pr-4">Stripe subscription</th>
                            <th className="py-2 pr-4"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td className="py-2 pr-4">
                                    {row.userName || "—"}{" "}
                                    <span className="text-gray-400">
                                        #{row.userId}
                                    </span>
                                </td>
                                <td className="py-2 pr-4 font-medium">
                                    {tierLabel(row.subscriptionType)}
                                </td>
                                <td className="py-2 pr-4">
                                    {row.active ? (
                                        <span className="text-green-600">
                                            active
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">
                                            ended
                                        </span>
                                    )}
                                </td>
                                <td className="py-2 pr-4">
                                    {row.startDate?.slice(0, 10) || "—"}
                                </td>
                                <td className="py-2 pr-4">
                                    {row.endDate?.slice(0, 10) || "—"}
                                </td>
                                <td className="py-2 pr-4 font-mono text-xs">
                                    {row.stripeSubscriptionId}
                                </td>
                                <td className="py-2 pr-4">
                                    <div className="flex items-center gap-3">
                                        <a
                                            href={dashboardHref(row)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                                        >
                                            Open in Stripe
                                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                        </a>
                                        <button
                                            onClick={() => reconcile(row.userId)}
                                            disabled={reconcilingId !== null}
                                            className="text-indigo-600 hover:underline disabled:opacity-50"
                                            title="Live-fetch this user's subscriptions from Stripe and repair the local grant"
                                        >
                                            {reconcilingId === row.userId
                                                ? "Reconciling…"
                                                : "Reconcile"}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && !error && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="py-8 text-center text-gray-400"
                                >
                                    No Stripe subscriptions yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default StripeSubscriptionPage;
